import {
  type Except,
  getObjectEntries,
  type Simplify,
} from '@festina-life/core'
import type { ComponentProps, JSX, JSXElementConstructor } from 'react'

const UNSAFE_PROPS = [
  'dangerouslySetInnerHTML',
  'suppressContentEditableWarning', // We shouldn't need to do that so prevent passing it by mistake
  'suppressHydrationWarning', // We shouldn't need to do that so prevent passing it by mistake
  // We could consider removing all *Capture events like e.g. onClickCapture since we shouldn't really need those and they can be confusing when used by mistake
] as const

type UnsafeProps = (typeof UNSAFE_PROPS)[number]

type AlwaysPromotedProps =
  | 'ref' // is always added as part of PropsWithSafeExtra
  | 'key' // always added by react

/**
 * Utility to ensure runtime safety when spreading props on components.
 */
export const safeRestProps = <
  Props1 extends Partial<Record<string, unknown>>,
  Props2 extends Partial<Record<string, unknown>>,
>(
  props1: Props1,
  props2?: Props2,
) =>
  Object.fromEntries(
    getObjectEntries({ ...props1, ...props2 }).filter(
      ([key]) => !UNSAFE_PROPS.includes(key as UnsafeProps),
    ),
  ) as Except<Props1 & Props2, UnsafeProps>

/**
 * Type utility to ensure type safety when spreading props on components. It promotes the explicitly defined props and ref of the component and requires any extra props to be passed in an `extraProps` object, which is then filtered at runtime by `safeRestProps` to ensure that no unsafe props are passed to the underlying component.
 *
 * The generic parameters are:
 * - `T`: The type of the component, which can be a string for intrinsic elements (e.g. 'input', 'div', etc.) or a React component type.
 * - `K`: The keys of the props of the component that should be explicitly defined and promoted to the top level of the resulting props type.
 * - `P`: An optional type for any additional props that should be included in the resulting props type, which can be used to allow for additional custom props while still ensuring that unsafe props are not included.
 */
export type PropsWithSafeExtra<
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  T extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>,
  K extends keyof ComponentProps<T>,
  P = unknown, // We don't care about this type, it's just to allow extending it
> = Simplify<
  P &
    Pick<ComponentProps<T>, K | AlwaysPromotedProps> & {
      extraProps?: Except<
        ComponentProps<T>,
        K | UnsafeProps | AlwaysPromotedProps
      >
    }
>
